import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import type { NostrEvent } from '@nostrify/nostrify';

interface FollowList {
  event: NostrEvent | null;
  follows: string[];
}

/**
 * Hook to get the current user's follow list (kind 3).
 */
export function useFollowList() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useQuery<FollowList>({
    queryKey: ['follow-list', user?.pubkey],
    enabled: !!user?.pubkey,
    queryFn: async ({ signal }) => {
      if (!user?.pubkey) {
        return { event: null, follows: [] };
      }

      const events = await nostr.query(
        [{ kinds: [3], authors: [user.pubkey], limit: 1 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(5000)]) }
      );

      const event = events[0] ?? null;
      const follows = event?.tags
        .filter((tag) => tag[0] === 'p')
        .map((tag) => tag[1]) ?? [];

      return { event, follows };
    },
  });
}

/**
 * Hook to check if the current user is following a specific pubkey.
 */
export function useIsFollowing(pubkey: string) {
  const { data } = useFollowList();
  return data?.follows.includes(pubkey) ?? false;
}

/**
 * Hook to follow/unfollow a pubkey.
 */
export function useFollowMutation() {
  const { mutateAsync: publishEvent } = useNostrPublish();
  const { data: followList } = useFollowList();
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();

  return useMutation({
    mutationFn: async ({ pubkey, action }: { pubkey: string; action: 'follow' | 'unfollow' }) => {
      if (!user?.pubkey) {
        throw new Error('You must be logged in to follow/unfollow users');
      }

      // Get current follow list tags
      const currentTags = followList?.event?.tags.filter((tag) => tag[0] === 'p') ?? [];
      
      let newTags: string[][];

      if (action === 'follow') {
        // Check if already following
        const alreadyFollowing = currentTags.some((tag) => tag[1] === pubkey);
        if (alreadyFollowing) {
          return; // Already following, no need to update
        }
        // Add new follow
        newTags = [...currentTags, ['p', pubkey]];
      } else {
        // Remove from follows
        newTags = currentTags.filter((tag) => tag[1] !== pubkey);
      }

      // Publish updated follow list
      await publishEvent({
        kind: 3,
        content: followList?.event?.content ?? '',
        tags: newTags,
      });
    },
    onSuccess: () => {
      // Invalidate follow list queries
      queryClient.invalidateQueries({ queryKey: ['follow-list', user?.pubkey] });
    },
  });
}
